import { useEffect, useState } from 'react';
import { Canvas, FabricObject, TEvent } from 'fabric';
import { v4 as uuidv4 } from 'uuid';
import { createBackgroundRect, isSystemPiece } from '../../canvas/utils/canvas-pieces';
import {
  NEW_TEXT_CONTENT,
  NEW_TEXT_WIDTH,
  createSobreimpreso,
  SOBREIMPRESO_INSERT_DEFAULTS,
  type SobreimpresoCreateConfig
} from '../../text';
import { createPill, PILL_INSERT_DEFAULTS } from '../../pill';
import { createAutoFitText, AUTO_FIT_TEXT_INSERT_DEFAULTS } from '../../auto-fit-text';
import { usePieceDispatch } from '../../model/piece-context';
import type { ShapeLayer, TextLayer } from '../../model/types';
import { addSvgToCanvas } from '../../svg-import';
import { createOwnLaneKey } from '../../timeline/utils/timeline-rows';

type UseCanvasObjectsOptions = {
  designWidth: number;
  designHeight: number;
};

export const useCanvasObjects = (canvas: Canvas | null, { designWidth, designHeight }: UseCanvasObjectsOptions) => {
  const [selectedObject, setSelectedObject] = useState<FabricObject | null>(null);
  const dispatch = usePieceDispatch();

  // Keeps the selected object in sync with the canvas selection.
  useEffect(() => {
    if (!canvas) return;

    const handleSelection = (
      e: Partial<TEvent> & {
        selected: FabricObject[];
      }
    ) => setSelectedObject(e.selected?.[0] ?? null);
    const handleCleared = () => setSelectedObject(null);

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    canvas.on('selection:cleared', handleCleared);

    return () => {
      canvas.off('selection:created', handleSelection);
      canvas.off('selection:updated', handleSelection);
      canvas.off('selection:cleared', handleCleared);
    };
  }, [canvas]);

  useEffect(() => {
    if (!canvas) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      const active = canvas.getActiveObject() as
        | (FabricObject & { isEditing?: boolean; exitEditing?: () => void })
        | null;
      if (active?.isEditing) {
        active.exitEditing?.();
        canvas.requestRenderAll();
        return;
      }

      const activeObjects = canvas.getActiveObjects();
      if (activeObjects.length === 0) return;

      const removable = activeObjects.filter(obj => obj.deletable !== false);
      if (removable.length === 0) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        return;
      }

      removable.forEach(obj => canvas.remove(obj));
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canvas]);

  const centerX = designWidth / 2;
  const centerY = designHeight / 2;

  const baseVisual = () => ({
    id: uuidv4(),
    visible: true,
    locked: false,
    left: centerX,
    top: centerY,
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    opacity: 1,
    start: 0,
    duration: 0,
    fit: 'fill' as const
  });

  const addRectangle = () => {
    const layer: ShapeLayer = {
      ...baseVisual(),
      kind: 'shape',
      content: {
        shape: 'rect',
        width: 150,
        height: 100,
        fill: { kind: 'solid', color: '#3b82f6' },
        stroke: '#1e40af',
        strokeWidth: 2
      }
    };
    dispatch({ type: 'addLayer', layer });
  };

  const addCircle = () => {
    const layer: ShapeLayer = {
      ...baseVisual(),
      kind: 'shape',
      content: {
        shape: 'circle',
        radius: 50,
        fill: { kind: 'solid', color: '#ef4444' },
        stroke: '#991b1b',
        strokeWidth: 2
      }
    };
    dispatch({ type: 'addLayer', layer });
  };

  const addText = () => {
    const layer: TextLayer = {
      ...baseVisual(),
      kind: 'text',
      // Own track-row, kept across save/reload — see timeline/utils/timeline-rows.ts.
      laneKey: createOwnLaneKey(),
      content: {
        text: NEW_TEXT_CONTENT,
        fontFamily: 'Arial',
        fontSize: 50,
        fontWeight: 'bold',
        width: NEW_TEXT_WIDTH,
        fill: { kind: 'solid', color: '#c077ff' }
      }
    };
    dispatch({ type: 'addLayer', layer });
  };

  const addPill = () => {
    if (!canvas) return;
    const pill = createPill({ ...PILL_INSERT_DEFAULTS, left: centerX, top: centerY });
    pill.laneKey = createOwnLaneKey();
    canvas.add(pill);
  };

  const addSvg = async (svgText: string) => {
    if (!canvas) return;
    await addSvgToCanvas(canvas, svgText, { left: centerX, top: centerY });
  };

  const addSobreimpreso = (config?: Omit<SobreimpresoCreateConfig, 'left' | 'top'>) => {
    if (!canvas) return;
    const sobreimpreso = createSobreimpreso({ ...(config ?? SOBREIMPRESO_INSERT_DEFAULTS), left: centerX, top: centerY });
    sobreimpreso.laneKey = createOwnLaneKey();
    canvas.add(sobreimpreso);
  };

  const addAutoFitText = () => {
    if (!canvas) return;
    const autoFitText = createAutoFitText({ ...AUTO_FIT_TEXT_INSERT_DEFAULTS, left: centerX, top: centerY });
    autoFitText.laneKey = createOwnLaneKey();
    canvas.add(autoFitText);
  };

  const deleteSelected = () => {
    if (!canvas) return;

    const activeObjects = canvas.getActiveObjects();
    if (!activeObjects.length) return;

    // System pieces (backgrounds, effect overlays) have dedicated UI; deletable=false opts out.
    const removable = activeObjects.filter(obj => !isSystemPiece(obj) && obj.deletable !== false);
    if (!removable.length) {
      canvas.discardActiveObject();
      canvas.requestRenderAll();
      return;
    }

    removable.forEach(obj => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  };

  const clearCanvas = () => {
    if (!canvas) return;

    const vpt = canvas.viewportTransform ? ([...canvas.viewportTransform] as typeof canvas.viewportTransform) : null;

    canvas.clear();
    canvas.backgroundColor = 'transparent';

    if (vpt) canvas.setViewportTransform(vpt);

    const background = createBackgroundRect(designWidth, designHeight, '#ffffff');
    canvas.add(background);
    canvas.sendObjectToBack(background);

    canvas.requestRenderAll();
  };

  return {
    selectedObject,
    addRectangle,
    addCircle,
    addText,
    addPill,
    addSvg,
    addSobreimpreso,
    addAutoFitText,
    deleteSelected,
    clearCanvas
  };
};
